// import { DeleteForeverTwoTone } from "@mui/icons-material";
// import { Box, Chip, IconButton } from "@mui/material";
// import { DataGrid } from "@mui/x-data-grid/DataGrid";
// import { useCallback, useState } from "react";
// import { logDebug } from "../../utils/logger";

// const GiftCardTable: React.FC = () => {
//     const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
//     const [loading, setLoading] = useState(false);
//     const handlePaginationModelChange = (newPaginationModel: any) => {
//         logDebug("Pagination model changed", newPaginationModel);
//     };

//     const deleteGiftCard = useCallback((giftCard: any) => {
//         logDebug("delete gift card", giftCard);
//     }, []);

//     const columns = [
//         {
//             field: 'id',
//             headerName: 'Secret Key',
//             flex: 2,
//             renderCell: (params: any) => (
//                 <Box>
//                     <IconButton>
//                         <DeleteForeverTwoTone onClick={() => deleteGiftCard(params.row)} />
//                     </IconButton>
//                     {params.value}
//                 </Box>
//             )
//         },
//         { field: 'hash', headerName: 'Barcode', flex: 1 },
//         { field: 'dlcId', headerName: 'DLC ID', flex: 1 },
//         {
//             field: 'status',
//             headerName: 'Status',
//             flex: 1,
//             renderCell: (params: any) => {
//                 let color: 'success' | 'error' | 'default' | 'warning' = 'default';
//                 switch (params.value.toLowerCase()) {
//                     case 'active': color = 'success'; break;
//                     case 'inactive': color = 'error'; break;
//                     case 'redeemed': color = 'warning'; break;
//                     case 'expired': color = 'default'; break;
//                 }
//                 return <Chip label={params.value} color={color} />;
//             },
//         },
//         { field: 'expirationDate', headerName: 'Expiration Date', flex: 1 },
//     ];


//     return (
//         <>
//             {/* <ConfirmDialog
//             /> */}
//             <DataGrid 
//                 rows={[]}
//                 columns={columns}
//                 pageSizeOptions={[5, 10, 20, 100]}
//                 rowCount={-1}
//                 paginationMode="server"
//                 paginationModel={paginationModel}
//                 onPaginationModelChange={handlePaginationModelChange}
//                 loading={loading}
//             />
//         </>
//     )
// }

// export default GiftCardTable;

import { DeleteForeverTwoTone } from '@mui/icons-material';
import { Box, Chip, IconButton } from '@mui/material';
import { DataGrid, GridPaginationMeta, GridPaginationModel } from '@mui/x-data-grid';
import React from 'react';
import { deleteInactiveGC, getGiftCards } from '../../api/admin';
import { useAuth } from '../../contexts/AuthContext';
import { GiftCard } from '../../types/api';
import { logDebug, logInfo } from '../../utils/logger';
import { showErrorToast, showSuccessToast } from '../../utils/toastUtils';
import ConfirmDialog from './ConfirmDialog';

const PAGE_SIZE = 25;

const GiftCardTable: React.FC = () => {
    const [giftcards, setGiftCards] = React.useState<GiftCard[]>([]);
    const [openDialog, setOpenDialog] = React.useState<boolean>(false);
    const [selectedGiftCard, setSelectedGiftCard] = React.useState<GiftCard | null>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    
    const { idToken } = useAuth();

    // Cursor-based pagination tracking
    const mapPageToNextCursor = React.useRef<{ [page: number]: string }>({});

    // Single pagination model for DataGrid
    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: PAGE_SIZE,
    });

    // Track pagination metadata
    const paginationMetaRef = React.useRef<GridPaginationMeta>({});

    // Stores whether there's another page to fetch
    const [hasNextPage, setHasNextPage] = React.useState<boolean | undefined>(undefined);
    const [totalRowCount, setTotalRowCount] = React.useState<number>(-1);

    // **Fetch Gift Cards Function**
    const fetchGiftCards = async () => {
        try {
            logInfo("Fetching gift cards", paginationModel.page);
            if (!idToken) return;
            setLoading(true);

            const cursor = mapPageToNextCursor.current[paginationModel.page - 1] || undefined;
            const response = await getGiftCards(idToken, paginationModel.pageSize, cursor);

            setTimeout(() => {
                setGiftCards(response.data.giftcards);
                setHasNextPage(response.continuation_token !== undefined);
                setTotalRowCount(response.data.giftcards.length ?? -1);
            }, 25)
            
            // Store next cursor if available
            if (response.continuation_token) {
                mapPageToNextCursor.current[paginationModel.page] = response.continuation_token;
            }
        } catch (e) {
            logDebug("Error fetching gift cards", e);
            showErrorToast("Failed to load gift cards");
        } finally {
            setLoading(false);
        }
    }
    // , [idToken]);

    // Trigger data fetch when page changes
    React.useEffect(() => {
        fetchGiftCards();
    }, [paginationModel.page]);

    // **Pagination Change Handler**
    const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
        // Only update if we have a cursor for the requested page
        if (newPaginationModel.page === 0 || mapPageToNextCursor.current[newPaginationModel.page - 1]) {
            logDebug("Pagination model changed", newPaginationModel);
            setPaginationModel(newPaginationModel);
            fetchGiftCards();
        }
    };

    // Memoized pagination meta to avoid flickering
    const paginationMeta = React.useMemo(() => {
        logInfo("Pagination meta updated", { hasNextPage, totalRowCount });
        if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
            paginationMetaRef.current = { hasNextPage };
        }
        return paginationMetaRef.current;
    }, [hasNextPage]);

    // **Handle Delete Confirm**
    const handleDialogConfirm = async () => {
        try {
            setOpenDialog(false);
            if (!selectedGiftCard) return;
            
            const msg = await deleteInactiveGC(idToken!, selectedGiftCard.id);
            showSuccessToast(msg.message ?? "Gift Card Deleted");

            // Refetch data after deletion
            fetchGiftCards();
        } catch (e) {
            logDebug("Error deleting gift card", e);
            showErrorToast("Failed to delete gift card");
        }
    };

    // **Table Columns**
    const columns = [
        {
            field: 'id',
            headerName: 'Secret Key',
            flex: 2,
            renderCell: (params: any) => (
                <Box>
                    <IconButton>
                        <DeleteForeverTwoTone onClick={() => setSelectedGiftCard(params.row)} />
                    </IconButton>
                    {params.value}
                </Box>
            )
        },
        { field: 'hash', headerName: 'Barcode', flex: 1 },
        { field: 'dlcId', headerName: 'DLC ID', flex: 1 },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            renderCell: (params: any) => {
                let color: 'success' | 'error' | 'default' | 'warning' = 'default';
                switch (params.value.toLowerCase()) {
                    case 'active': color = 'success'; break;
                    case 'inactive': color = 'error'; break;
                    case 'redeemed': color = 'warning'; break;
                    case 'expired': color = 'default'; break;
                }
                return <Chip label={params.value} color={color} />;
            },
        },
        { field: 'expirationDate', headerName: 'Expiration Date', flex: 1 },
    ];

    return (
        <>
            <ConfirmDialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                onConfirm={handleDialogConfirm}
                message="Are you sure you want to delete this gift card?"
            />
            <DataGrid
                rows={giftcards}
                columns={columns}
                pageSizeOptions={[PAGE_SIZE]}
                rowCount={-1}
                paginationMeta={paginationMeta}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                loading={loading}
            />
        </>
    );
};

export default GiftCardTable;